import React from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';

import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';
import IbTypography from '../../../../../components/common/IbTypography';

import {
  FLOW_CHART_EMPTY_CLASS_NAME,
  FLOW_CHART_HANDLE_CLASS_NAME,
  FLOW_CHART_NO_INTERACTION_CLASS_NAME,
  FLOW_CHART_NODE_ACTION_CLASS_NAME,
  FLOW_CHART_NODE_CLASS_NAME,
  FLOW_CHART_NODE_EXTERNAL_ACTION_CLASS_NAME,
  FLOW_CHART_NODE_MESSAGE_CLASS_NAME,
  FLOW_CHART_STAGE_CLASS_NAME,
  FLOW_CHART_STAGE_STEPS_CLASS_NAME,
  FLOW_CHART_STAGE_TITLE_CLASS_NAME,
  FLOW_CHART_STEP_ADD_CLASS_NAME,
  FLOW_CHART_STEP_CLASS_NAME,
  FLOW_CHART_STEP_DISABLED_CLASS_NAME,
  FLOW_CHART_STEP_INDEX_CLASS_NAME,
  FLOW_CHART_STEP_TITLE_CLASS_NAME,
} from './const';
import { IFlowBaseNodeData, IFlowStageNodeData } from './types';

const FlowHandles = () => (
  <>
    <Handle className={FLOW_CHART_HANDLE_CLASS_NAME} id="left" position={Position.Left} type="target" />
    <Handle className={FLOW_CHART_HANDLE_CLASS_NAME} id="right" position={Position.Right} type="source" />
    <Handle className={FLOW_CHART_HANDLE_CLASS_NAME} id="top" position={Position.Top} type="target" />
    <Handle className={FLOW_CHART_HANDLE_CLASS_NAME} id="bottom" position={Position.Bottom} type="source" />
    <Handle className={FLOW_CHART_HANDLE_CLASS_NAME} id="bottom-target" position={Position.Bottom} type="target" />
  </>
);

const FlowBaseNode: React.FC<NodeProps<IFlowBaseNodeData>> = ({ data }) => {
  const className = [
    FLOW_CHART_NODE_CLASS_NAME,
    data.kind === 'message'
      ? FLOW_CHART_NODE_MESSAGE_CLASS_NAME
      : data.kind === 'external-action'
      ? FLOW_CHART_NODE_EXTERNAL_ACTION_CLASS_NAME
      : FLOW_CHART_NODE_ACTION_CLASS_NAME,
    data.disabled ? FLOW_CHART_STEP_DISABLED_CLASS_NAME : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <FlowHandles />
      <IbTypography.Paragraph strong>{data.title}</IbTypography.Paragraph>
    </div>
  );
};

const FlowStageNode: React.FC<NodeProps<IFlowStageNodeData>> = ({ data }) => {
  const onAddButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    data.onAddClick();
  };

  return (
    <div className={FLOW_CHART_STAGE_CLASS_NAME}>
      <FlowHandles />
      <div className={FLOW_CHART_STAGE_TITLE_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">{data.title}</IbTypography.Paragraph>
      </div>
      <div
        className={`${FLOW_CHART_STAGE_STEPS_CLASS_NAME} ${FLOW_CHART_NO_INTERACTION_CLASS_NAME}`}
        onWheel={(event) => event.stopPropagation()}
      >
        {!data.steps.length ? (
          <div className={FLOW_CHART_EMPTY_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{data.emptyLabel}</IbTypography.Paragraph>
          </div>
        ) : (
          data.steps.map((step, index) => {
            const className = [FLOW_CHART_STEP_CLASS_NAME, !step.enabled ? FLOW_CHART_STEP_DISABLED_CLASS_NAME : '']
              .filter(Boolean)
              .join(' ');

            return (
              <div key={step.key} className={className}>
                <span className={FLOW_CHART_STEP_INDEX_CLASS_NAME}>{index + 1}.</span>
                <span className={FLOW_CHART_STEP_TITLE_CLASS_NAME}>
                  <IbTypography.Paragraph ellipsis strong type="secondary">
                    {step.title}
                  </IbTypography.Paragraph>
                </span>
              </div>
            );
          })
        )}
        <IbButton
          className={`${FLOW_CHART_STEP_ADD_CLASS_NAME} ${FLOW_CHART_NO_INTERACTION_CLASS_NAME}`}
          disabled={data.disabled}
          icon={<IbIcon iconName="add-one" />}
          type="link"
          onClick={onAddButtonClick}
        >
          {data.addLabel}
        </IbButton>
      </div>
    </div>
  );
};

export const flowNodeTypes = {
  base: FlowBaseNode,
  stage: FlowStageNode,
};
